import { Injectable } from "@angular/core";
import { UserInfoData, UserDetailInfoViewModel, FurtherAddressViewModel } from "../../shared/models/userInfo.model";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import * as _ from "lodash";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { IObjectObserver } from "../../services/IObjectObserver";
import { CustomerInputFieldViewModel, OrderHistoryDataModel, OrderHistoryViewModel } from "../../shared/models";
import { ContentType } from "../../shared/contentTypes";
import { Observable } from "rxjs";

@Injectable()
export class UserProfileDetailService {
  /** OrderHistory Observer */
  private _orderHistoryObsServ: IObjectObserver<OrderHistoryDataModel>;
  /** Currency Symbol */
  private _currencySymbol: string;
  public get currencySymbol(): string {
    return this._currencySymbol;
  }
  /** Is order empty-boolean */
  private _isOrderEmpty: boolean;
  /** get Isorderempty-boolean */
  public get isOrderEmpty(): boolean {
    return this._isOrderEmpty;
  }

  constructor(private _circularInjectionHelper: CircularInjectionHelper) {
    const me = this;
    me._orderHistoryObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<OrderHistoryDataModel>(ContentType.OrderHistory, me._circularInjectionHelper);
  }

  /**
   * save and edit user info
   * @userInfoViewModel userviewmodel data is sent from component
   */
  public saveUserInfo(userInfoViewModel: UserDetailInfoViewModel): Promise<void> {
    const me = this;
    userInfoViewModel.bday_ymd = userInfoViewModel.bday_ymd || 0;
    return new Promise<void>((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.User.SaveUserInfo(userInfoViewModel)
        .then(() => {
          resolve();
        })
        .catch(error => {
          me._circularInjectionHelper.coreLogService.error("UserProfileDetailService.saveUserInf)", "_circularInjectionHelper.yourAppServer.User.SaveUserInfo", error);
        })
    });
  }

  public GetUserDetailInfoViewModel(): Promise<UserDetailInfoViewModel> {
    const me = this;
    return new Promise((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.User.GetUserInfo(true)
        .then((userInfo) => {
          resolve(me.mapToUserDetailInfoViewModel(userInfo));
        })
        .catch((error) => {
          reject();
          this._circularInjectionHelper.coreLogService.error("UserHelperService", "_circularInjectionHelper.yourAppServer.User.GetUserInfo()", error);
        });
    });
  }

  // mapper
  private mapToUserDetailInfoViewModel(model: UserInfoData): UserDetailInfoViewModel {
    const viewModel = new UserDetailInfoViewModel();
    viewModel.fname = model.fname || "";
    viewModel.name = model.name || "";
    viewModel.bday_ymd = model.bday_ymd;
    viewModel.gender = model.gender;
    viewModel.email = model.email;
    viewModel.telephone1 = model.telephone1 || "";
    viewModel.telephone_mobile = model.telephone_mobile || "";
    viewModel.address = model.address || "";
    viewModel.addition = model.addition || "";
    viewModel.co = model.co || "";
    viewModel.zip_code = model.zip_code || "";
    viewModel.city = model.city || "";
    viewModel.country = model.country || "";
    if (model.further_addresses && model.further_addresses.length > 0) {
      viewModel.furtherAddress = [];
      model.further_addresses.forEach(address => {
        const newDeliveryAddress = new FurtherAddressViewModel();
        newDeliveryAddress.id = address.token;
        newDeliveryAddress.addition = address.addition || "";
        newDeliveryAddress.address = address.address || "";
        newDeliveryAddress.city = address.city || "";
        newDeliveryAddress.co = address.co || "";
        newDeliveryAddress.country = address.country || "";
        newDeliveryAddress.zip_code = address.zip || "";
        viewModel.furtherAddress.push(newDeliveryAddress);
      })
    }
    return viewModel;
  }

  /**
   * Ermittelt die Bestellungen anhand des Filters (wie z.B. nach location und order_at)
   * (Deleted und status werden bereits berücksichtigt)
   */
  public GetOrderHistory(filter: (order: OrderHistoryViewModel) => boolean): Promise<OrderHistoryViewModel[]> {
    const me = this;
    return new Promise<OrderHistoryViewModel[]>((resolve, reject) => {
      me._orderHistoryObsServ.GetObjectsByFilter((order) => !order.deleted && order.status != 0 && filter(order))
        .then((orders) => {
          if (orders.length > 0) {
            me._isOrderEmpty = false;
            me._currencySymbol = orders[0].currency;
          } else {
            me._isOrderEmpty = true;
          }
          me.mapDataModelListToViewModelList(orders).then
            // todo orders.map() to View Model
            (resolve);
        }).catch(reject);
    });
  }

  /** Triggers if OrderHistory has changes (keys form changed orders) */
  public get OrderHistoryChanges(): Observable<(string | number)[]> {
    return this._orderHistoryObsServ.ObserveChanges;
  }

  /**
  * map a OrderHistoryDataModel to OrderHistoryViewModel
  * @param model Get a OrderHistoryDataModel for mapping
  * @result return a OrderHistoryViewModel
  */
  private async mapDataModelToViewModel(model: OrderHistoryDataModel): Promise<OrderHistoryViewModel> {
    const viewModel = new OrderHistoryViewModel();
    viewModel.id = model.id;
    viewModel.location = model.location;
    viewModel.order_at_ymd = model.order_at_ymd;
    viewModel.order_at = model.order_at;
    viewModel.order_type = model.order_type;
    viewModel.loc_label_de = model.loc_label_de;
    viewModel.loc_label_en = model.loc_label_en;
    viewModel.payment_amount = model.payment_amount;
    viewModel.currency = model.currency;
    return viewModel;
  }

  /**
  * map a list of  OrderHistoryDataModel to a list of OrderHistoryViewModel
  * @param model Get a list of OrderHistoryDataModel for mapping
  * @result return a list of OrderHistoryViewModel
  */
  private mapDataModelListToViewModelList(modelList: OrderHistoryDataModel[]): Promise<OrderHistoryViewModel[]> {
    const me = this;
    if (modelList) {
      return Promise.all(modelList.map(orders => me.mapDataModelToViewModel(orders)));
    } else {
      return Promise.resolve(null);
    }
  }

  /**
   * ! Before the change, the fields were directly bound in the template to the userDetailInfoViewModel
   * ! and were statically defined. This means that these values or fields were always displayed.
   * ! This behavior remained unchanged.
   *
   * Creates CustomerInputFieldViewModel array for user info fields
   * Used to configure dynamic form fields with validation
   */

  public buildUserInfoFieldsConfig(userDetailInfoViewModel: UserDetailInfoViewModel): CustomerInputFieldViewModel[] {
    const fields: CustomerInputFieldViewModel[] = [];

    // First name field
    fields.push({
      fieldName: 'fname',
      fieldType: 'text',
      isRequired: true,
      value: userDetailInfoViewModel.fname || ''
    });

    // Last name field
    fields.push({
      fieldName: 'name',
      fieldType: 'text',
      isRequired: true,
      value: userDetailInfoViewModel.name || ''
    });

    // Email field (disabled, read-only)
    fields.push({
      fieldName: 'email',
      fieldType: 'text',
      isRequired: false,
      value: userDetailInfoViewModel.email || ''
    });

    // Birthday field
    fields.push({
      fieldName: 'bday_ymd',
      fieldType: 'date',
      isRequired: false,
      value: userDetailInfoViewModel.bday_ymd ? userDetailInfoViewModel.bday_ymd.toString() : ''
    });

    // Gender field (using 'prefix' for compatibility with dynamicFormFields)
    fields.push({
      fieldName: 'prefix',
      fieldType: 'text',
      isRequired: false,
      value: userDetailInfoViewModel.gender ? userDetailInfoViewModel.gender.toString() : ''
    });

    return fields;
  }

}

